<template>
    <AxRoadPage :title="$t('truckDetails')" no-padding>
        <div v-if="truckDetails" class="truck-details">
            <div class="truck-details__picture">
                <img
                    :src="
                        truckDetails.picture ||
                            assetTypePicture ||
                            'https://place-hold.it/375x200'
                    "
                    alt="Truck Picture"
                    class="truck-details__img"
                />
            </div>
            <div class="l-stack l-gap-3 l-padded-y">
                <TruckDetailsWidget :tracker="tracker" />
                <TrailerDetailsWidget :trailer-asset="trailerAsset" />
                <template v-if="hasTachographAccess">
                    <VSpinner
                        v-if="isDriverLoading && !isRefreshingDriver"
                        size="medium"
                        line-fg-color="#000"
                        :speed="1"
                    />
                    <template v-else>
                        <DriverDetailsWidget
                            v-if="
                                driver &&
                                    truckDetails.sensor_data
                                        .driver_1_card_presence &&
                                    truckDetails.sensor_data
                                        .driver_1_card_presence.value === 1
                            "
                            :driver="driver"
                        />
                        <div
                            v-else-if="
                                truckDetails.sensor_data.driver_name &&
                                    truckDetails.sensor_data
                                        .driver_1_card_presence.value === 1
                            "
                            class="truck-details__section"
                        >
                            <div class="truck-details__title">
                                <DriverAvatar
                                    :title="
                                        truckDetails.sensor_data.driver_name
                                            .value
                                    "
                                />
                                <span>{{
                                    truckDetails.sensor_data.driver_name.value
                                }}</span>
                            </div>
                        </div>
                    </template>
                </template>
                <div class="truck-details__section">
                    <h1 class="truck-details__title">
                        <LocationTargetIcon width="24" height="24" />
                        {{ $t('cockpit') }}
                    </h1>
                    <div class="truck-details__cockpit">
                        <InfoBlock
                            v-for="item in cockpitItems"
                            :key="`${item.label}__${truckDetails.id}`"
                            :label="$t(item.label)"
                            :value="item.value"
                            :variant="item.variant"
                            outlined
                        >
                            <template #icon>
                                <component
                                    :is="item.icon"
                                    width="24"
                                    height="24"
                                    v-bind="item.iconProps || {}"
                                />
                            </template>
                            <template v-if="item.subtitle" #subtitle>
                                <span :class="item.subtitleClass">{{
                                    item.subtitle
                                }}</span>
                            </template>
                        </InfoBlock>
                    </div>
                </div>
                <div
                    v-if="
                        additionalData.length &&
                            additionalData.filter(f => f.value).length
                    "
                    class="truck-details__section truck-details__section"
                >
                    <h1 class="truck-details__title">
                        <InfoIcon width="24" height="24" />
                        <span>{{ $t('customerFields') }}</span>
                    </h1>
                    <div class="l-stack l-gap-2">
                        <CustomerField
                            v-for="(field, index) in additionalData"
                            :key="index"
                            :field="field"
                        />
                    </div>
                </div>
                <WarningsWidget
                    :entity-id="truckDetails.id"
                    entity-type="truck"
                />
            </div>
        </div>
    </AxRoadPage>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { throttle } from 'lodash'
import VSpinner from 'vue-simple-spinner'

import AssetHelpers from '@/mixins/AssetHelpers'
import AxRoadPage from '@/components/ax-road/AxRoadPage'
import OdometerIcon from '@/components/icons/OdometerIcon'
import CustomerField from '@/components/CustomerField'
import DriverAvatar from '@/components/ax-road/DriverAvatar'
import DriverDetailsWidget from '@/components/ax-road/DriverDetailsWidget'
import FuelCanIcon from '@/components/icons/FuelCanIcon'
import InfoBlock from '@/components/ax-road/InfoBlock'
import InfoIcon from '@/components/icons/InfoIcon'
import LocationTargetIcon from '@/components/icons/LocationTargetIcon'
import BatteryPlugIcon from '@/components/icons/BatteryPlugIcon'
import BatteryIcon from '@/components/icons/BatteryIcon'
import TrailerDetailsWidget from '@/components/ax-road/TrailerDetailsWidget'
import TruckDetailsWidget from '@/components/ax-road/TruckDetailsWidget'
import WarningsWidget from '@/components/ax-road/WarningsWidget'
import { formatHelper } from '@/utils'
import moment from 'moment'

const excludedAdditionalDataFields = [
    'payload',
    'total_weight_truck',
    'total_weight_maximum_combined',
    'unladen_weight_semi_truck',
]

export default {
    name: 'TruckDetailsView',
    components: {
        AxRoadPage,
        BatteryIcon,
        CustomerField,
        DriverAvatar,
        DriverDetailsWidget,
        FuelCanIcon,
        BatteryPlugIcon,
        InfoBlock,
        InfoIcon,
        LocationTargetIcon,
        OdometerIcon,
        TrailerDetailsWidget,
        TruckDetailsWidget,
        VSpinner,
        WarningsWidget,
    },
    mixins: [AssetHelpers],
    data() {
        return {
            tracker: null,
            truckDetails: null,
            isRefreshingDriver: false, // no need for spinner when refreshing
        }
    },
    computed: {
        ...mapState('tracker', ['isDriverLoading']),
        ...mapGetters('tracker', [
            'assetsById',
            'assetsByAssetId',
            'assetTypesById',
            'driversById',
        ]),
        ...mapGetters('axroad', ['isDriverTimesFetching']),
        ...mapGetters('auth', ['hasTachographAccess']),
        assetTypePicture() {
            return this.assetTypesById[this.truckDetails?.asset_type]?.picture
                ?.url
        },
        trailerAsset() {
            const currentTrailer = this.tracker?.current_trailer
            const knownBeacons = this.tracker?.known_beacons_in_range || []
            if (typeof currentTrailer === 'number') {
                return this.assetsByAssetId[currentTrailer]?.asset_details
            } else if (!currentTrailer && knownBeacons) {
                return this.assetsById[knownBeacons[0]]?.asset_details
            }
            return currentTrailer
        },
        additionalData() {
            return this.getCustomerData(this.tracker).filter(
                field =>
                    field.value !== undefined &&
                    field.value !== null &&
                    field.value !== '' &&
                    !excludedAdditionalDataFields.includes(field.key)
            )
        },
        driver() {
            return (
                this.driversById[
                    this.truckDetails?.sensor_data?.driver_id?.value
                ] || {}
            )
        },
        cockpitItems() {
            const {
                fuel_level_liters,
                fuel_level,
                adblue_level,
                ev_charging_state,
                ev_charging_eta,
                ev_battery_percentage,
                ev_remaining_distance,
                external_voltage,
                odometer,
            } = this.truckDetails?.sensor_data || {}

            const now = moment()
            const chargingEta =
                ev_charging_state?.value && ev_charging_eta?.value
                    ? moment(ev_charging_eta.value)
                    : null
            return [
                {
                    show: !!fuel_level_liters?.value,
                    label: 'shared.measurements.fuel_level_liters',
                    value: `${fuel_level_liters?.value} L`,
                    icon: FuelCanIcon,
                    variant: 'centered',
                },
                {
                    show: !!fuel_level?.value,
                    label: 'shared.measurements.fuel_level',
                    value: fuel_level?.value,
                    icon: FuelCanIcon,
                    variant: 'diagram',
                },
                {
                    show: !!adblue_level?.value,
                    label: 'shared.measurements.adblue_level',
                    value: adblue_level?.value,
                    icon: FuelCanIcon,
                    variant: 'diagram',
                },
                {
                    show: ev_charging_state?.value !== undefined,
                    label: 'shared.measurements.ev_charging_state',
                    value: ev_charging_state?.value
                        ? this.$t('charging')
                        : this.$t('notCharging'),
                    ...(chargingEta?.isValid() && {
                        subtitle: chargingEta?.isAfter(now)
                            ? this.$t('readyIn', [
                                  formatHelper.hoursAndMinutesDuration(
                                      chargingEta.diff(now, 'seconds', true)
                                  ),
                              ])
                            : this.$t('ready'),
                        subtitleClass: 'pulsating text-center',
                    }),
                    icon: BatteryPlugIcon,
                    iconProps: {
                        class: { pulsating: !!ev_charging_state?.value },
                        color: ev_charging_state?.value ? '#41b883' : '#d32f2f',
                    },
                    variant: 'centered',
                },
                {
                    show: !!ev_battery_percentage?.value,
                    label: 'shared.measurements.ev_battery_percentage',
                    value: ev_battery_percentage?.value,
                    icon: BatteryIcon,
                    iconProps: {
                        batteryLevelInPercent: ev_battery_percentage?.value,
                    },
                    variant: 'diagram',
                },
                {
                    show: !!ev_remaining_distance?.value,
                    label: 'shared.measurements.ev_remaining_distance',
                    value: this.formatDistance(ev_remaining_distance?.value),
                    icon: OdometerIcon,
                    variant: 'centered',
                },
                {
                    show: !!external_voltage?.value,
                    label: 'shared.measurements.battery_voltage',
                    value: `${external_voltage?.value} V`,
                    icon: BatteryIcon,
                    variant: 'centered',
                },
                {
                    show: !!odometer?.value,
                    label: 'kmTotal',
                    value: this.formatDistance(odometer?.value),
                    icon: OdometerIcon,
                    variant: 'centered',
                },
            ].filter(item => item.show)
        },
    },
    watch: {
        '$route.params.id': {
            handler(id) {
                this.fetchTruckDetails()

                // clear any previous watcher first if needed
                if (this._assetUnwatch) this._assetUnwatch()

                // watch dynamically chosen key
                this._assetUnwatch = this.$watch(
                    () => this.assetsById[id],
                    ({ id: newId }, { id: oldId }) => {
                        if (newId !== oldId) {
                            this.isRefreshingDriver = false
                            this.fetchDriverDetails()
                        } else {
                            this.fetchDriverDetailsThrottled()
                        }
                    },
                    { deep: true }
                )
            },
            immediate: true,
        },
    },
    beforeRouteLeave(to, from, next) {
        this.setActiveTrackerOnMap(null)
        if (this._assetUnwatch) {
            this._assetUnwatch()
        }
        next()
    },
    methods: {
        ...mapMutations('tracker', [
            'setActiveTrackerOnMap',
            'setDriverById',
            'setShouldFollowActiveTrackerUpdates',
        ]),
        ...mapMutations('axroad', ['setCurrentDriverId']),
        ...mapActions('tracker', ['loadDriver']),
        ...mapActions('axroad', ['fetchDriverTimes']),
        formatDistance(distance) {
            return distance
                ? `${(distance / 1000).toFixed(2)} ${this.$t(
                      'shared.units.km'
                  )}`
                : '-'
        },
        async fetchDriverDetails() {
            const driverId = this.truckDetails.sensor_data.driver_id?.value
            if (this.hasTachographAccess && driverId) {
                this.setCurrentDriverId(driverId)
                await this.loadDriver(driverId)
                this.fetchDriverTimes(driverId)
            }
        },
        fetchDriverDetailsThrottled: throttle(
            async function() {
                this.isRefreshingDriver = true
                await this.fetchDriverDetails()
                this.isRefreshingDriver = false
            },
            30000 // We get minute-precision, no need to refresh more often than 30s
        ),
        fetchTruckDetails() {
            this.tracker = this.assetsById[this.$route.params.id]
            this.truckDetails = this.tracker?.asset_details

            this.setActiveTrackerOnMap(null)
            this.setShouldFollowActiveTrackerUpdates(true)

            if (!this.tracker) {
                return
            }

            this.fetchDriverDetails()

            this.$nextTick(() => {
                if (this.tracker?.asset_details) {
                    this.setActiveTrackerOnMap(this.tracker)
                }
            })
        },
    },
}
</script>

<i18n>
{
    "en" : {
        "truckDetails": "Truck Details",
        "kmTotal": "Km Total",
        "customerFields": "Customer defined fields",
        "cockpit": "Cockpit",
        "charging": "Charging",
        "notCharging": "Not charging",
        "ready": "Ready",
        "readyIn": "Ready in {0}"
    },
    "de" : {
        "truckDetails": "Fahrzeugsdetail",
        "kmTotal": "Km Total",
        "customerFields": "Benutzerdefinierte Felder",
        "cockpit": "Cockpit",
        "charging": "Lädt",
        "notCharging": "Lädt nicht",
        "ready": "Bereit",
        "readyIn": "In {0} bereit"
    },
    "fr" : {
        "truckDetails": "Détails du camion",
        "kmTotal": "Km Total",
        "customerFields": "Champs définis par le client",
        "cockpit": "Cockpit",
        "charging": "En charge",
        "notCharging": "Non en charge",
        "ready": "Prêt",
        "readyIn": "Prêt dans {0}"
    },
    "it": {
        "truckDetails": "Dettagli del camion",
        "kmTotal": "Km Totali",
        "customerFields": "Campi definiti dal cliente",
        "cockpit": "Cockpit",
        "charging": "In carica",
        "notCharging": "Non in carica",
        "ready": "Pronto",
        "readyIn": "Pronto in {0}"
    }
}
</i18n>

<style lang="scss">
.pulsating {
    animation: pulsate forwards 2s infinite ease-in-out;
}

@keyframes pulsate {
    50% {
        opacity: 0.5;
    }
}

.text-center {
    text-align: center;
}

.truck-details {
    &__img {
        width: 100%;
    }

    &__title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 700;
        line-height: 20px;

        &--padded {
            padding: 0 24px;
        }
    }

    &__icon {
        filter: invert(1) brightness(0);
    }

    &__ago {
        flex-grow: 1;
        text-align: right;
        font-weight: normal;
        color: $color-gray;
        font-size: 13px;
        white-space: nowrap;
    }

    &__section {
        padding: 0 24px 24px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        border-bottom: $style-border;

        &--nopadding {
            padding: 0;
        }
    }

    &__cockpit {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(calc(50% - 8px), 1fr));
        grid-template-rows: auto;
        gap: 16px;
        align-items: stretch;
    }
}
</style>
